import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRAND_LOGO, DemoChip } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users, UserPlus, ShieldCheck, Target, Headphones, Wallet, Bell,
  Upload, ArrowLeftRight, Scale, BarChart3, Settings, LogOut, Menu, Moon, Sun, Globe, ClipboardList, RefreshCw, BookOpen, ChevronDown,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["relationship_manager", "operations", "compliance", "administrator"] },
  { to: "/portfolio", label: "My Portfolio", icon: Wallet, roles: ["customer"] },
  { to: "/notifications", label: "Notifications", icon: Bell, roles: ["customer", "relationship_manager", "operations", "compliance", "administrator"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["relationship_manager", "operations", "compliance", "administrator"] },
  { to: "/corporate-onboarding", label: "Onboarding", icon: UserPlus, roles: ["customer", "relationship_manager", "administrator"] },
  { to: "/onboarding", label: "Onboarding Status", icon: ClipboardList, roles: ["customer", "relationship_manager", "administrator"] },
  { to: "/rekyc", label: "Re-KYC", icon: RefreshCw, roles: ["customer", "compliance", "operations", "administrator"] },
  { to: "/compliance", label: "Compliance Review", icon: ShieldCheck, roles: ["compliance", "operations", "administrator"] },
  { to: "/leads", label: "Leads", icon: Target, roles: ["relationship_manager", "operations", "compliance", "administrator"] },
  { to: "/service-requests", label: "Service Requests", icon: Headphones, roles: ["relationship_manager", "operations", "compliance", "administrator", "customer"] },
  { to: "/imports", label: "Offshore Imports", icon: Upload, roles: ["operations", "administrator"] },
  { to: "/bgiof", label: "BGIOF Transactions", icon: ArrowLeftRight, roles: ["operations", "relationship_manager", "compliance", "administrator", "customer"] },
  { to: "/reconciliation", label: "AUM Reconciliation", icon: Scale, roles: ["operations", "compliance", "administrator"] },
  { to: "/mis", label: "MIS", icon: BarChart3, roles: ["relationship_manager", "operations", "compliance", "administrator"] },
  { to: "/admin", label: "Administration", icon: Settings, roles: ["administrator"] },
  { to: "/manual", label: "User Manual", icon: BookOpen, roles: ["customer", "relationship_manager", "operations", "compliance", "administrator"] },
];

// Mobile bottom nav for customers (primary touch points)
const CUSTOMER_TABS = [
  { to: "/portfolio", label: "Portfolio", icon: Wallet, testid: "mobile-nav-portfolio" },
  { to: "/corporate-onboarding", label: "Onboarding", icon: UserPlus, testid: "mobile-nav-onboarding" },
  { to: "/service-requests", label: "Requests", icon: Headphones, testid: "mobile-nav-requests" },
  { to: "/bgiof", label: "BGIOF", icon: ArrowLeftRight, testid: "mobile-nav-bgiof" },
  { to: "/notifications", label: "Alerts", icon: Bell, testid: "mobile-nav-notifications" },
];

const ROLE_LABEL = {
  customer: "Customer", relationship_manager: "Relationship Manager", operations: "Operations",
  compliance: "Compliance", administrator: "Administrator",
};

function NavItems({ role, onNav, dark }) {
  const items = NAV.filter((n) => n.roles.includes(role));
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onNav}
          data-testid={`nav-${n.to.slice(1)}`}
          className={({ isActive }) =>
            `relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? (dark
                    ? "bg-white/10 text-white before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-full before:bg-gold"
                    : "bg-primary text-primary-foreground shadow-sm")
                : (dark ? "text-slate-300/90 hover:bg-white/5 hover:text-white" : "text-slate-600 dark:text-slate-300 hover:bg-secondary")
            }`}>
          <n.icon className={`h-4 w-4 shrink-0 ${""}`} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand({ dark }) {
  return (
    <div className={`flex items-center gap-3 px-5 py-5 border-b ${dark ? "border-white/10" : "border-border"}`}>
      <img src={BRAND_LOGO} alt="Barjeel" className="h-9 w-9 rounded-md object-contain bg-white p-0.5" />
      <div className="leading-tight">
        <div className={`font-display text-xl font-bold ${dark ? "text-white" : "text-primary"}`}>Barjeel</div>
        <div className={`text-[10px] ${dark ? "text-slate-400" : "text-muted-foreground"}`}>Technology by REEDOS</div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, reportingCcy, setReportingCcy, theme, setTheme } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isCustomer = user.role === "customer";

  const doLogout = () => { logout(); navigate("/login"); };

  const navRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const update = () => setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
    update();
    const t = setTimeout(update, 500);
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); clearTimeout(t); };
  }, [user.role]);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar — deep navy with gold active indicator */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-navy text-slate-200 z-40">
        <Brand dark />
        <div className="relative flex-1 min-h-0 flex flex-col">
          <div ref={navRef} className="flex-1 min-h-0 overflow-y-auto sidebar-scroll py-4"><NavItems role={user.role} dark /></div>
          {moreBelow && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 flex items-end justify-center bg-gradient-to-t from-[#0B192C] via-[#0B192C]/80 to-transparent" data-testid="nav-more-below-hint">
              <ChevronDown className="h-4 w-4 mb-1 text-gold animate-bounce" />
            </div>
          )}
        </div>
        <div className="p-3 border-t border-white/10 shrink-0">
          <div className="px-2 py-2 mb-2">
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="text-sm font-medium truncate text-white">{user.name}</div>
            <div className="overline-label text-gold">{ROLE_LABEL[user.role]}</div>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2 bg-transparent border-white/15 text-slate-200 hover:bg-white/10 hover:text-white" onClick={doLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 glass border-b border-border">
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
            <div className="flex items-center gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0 bg-navy text-slate-200 border-white/10">
                  <Brand dark />
                  <div className="py-4 overflow-y-auto sidebar-scroll max-h-[calc(100dvh-240px)]"><NavItems role={user.role} dark onNav={() => setOpen(false)} /></div>
                  <div className="p-3 border-t border-white/10">
                    <Button variant="outline" className="w-full justify-start gap-2 bg-transparent border-white/15 text-slate-200 hover:bg-white/10" onClick={doLogout}><LogOut className="h-4 w-4" /> Sign out</Button>
                  </div>
                </SheetContent>
              </Sheet>
              <img src={BRAND_LOGO} alt="Barjeel" className="h-7 w-7 rounded object-contain bg-white p-0.5 lg:hidden" />
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-muted-foreground">One Barjeel One Platform</span>
                <DemoChip className="hidden md:inline-flex" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-lg border border-border bg-card overflow-hidden" data-testid="currency-toggle">
                <Globe className="h-3.5 w-3.5 ml-2 text-muted-foreground" />
                {["USD", "AED", "INR"].map((c) => (
                  <button key={c} onClick={() => setReportingCcy(c)}
                    data-testid={`ccy-${c}`}
                    className={`px-2.5 py-1.5 text-xs font-num font-semibold transition-colors ${reportingCcy === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
                    {c}
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </header>
        <main className={`p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto overflow-x-hidden ${isCustomer ? "pb-24 lg:pb-8" : ""}`}>{children}</main>
      </div>

      {/* Mobile bottom nav — customers only */}
      {isCustomer && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-border" data-testid="customer-bottom-nav">
          <div className="grid grid-cols-5">
            {CUSTOMER_TABS.map((t) => {
              const active = loc.pathname === t.to;
              return (
                <NavLink key={t.to} to={t.to} data-testid={t.testid}
                  className="flex flex-col items-center justify-center gap-0.5 min-h-[60px] py-1.5">
                  <span className={`flex items-center justify-center w-11 h-7 rounded-full transition-colors ${active ? "bg-primary/10" : ""}`}>
                    <t.icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  </span>
                  <span className={`text-[10px] font-medium ${active ? "text-primary" : "text-muted-foreground"}`}>{t.label}</span>
                  {active && <span className="h-0.5 w-5 rounded-full bg-gold" />}
                </NavLink>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
